import React, { useContext, useEffect, useRef, useState } from 'react';
import setuHeaderImage from '../assets/setu-header-img.svg';
import setuLogoSmall from '../assets/setu-logo-small.svg';
import menu from '../assets/menu-icon.svg';
import cssClass from './Header.module.css';
import { UserContext } from '../context/UserContext';
import { AnimatePresence, motion } from 'framer-motion';
import { useLocation, useNavigate } from 'react-router-dom';
import useWindowDimensions from '../hooks/useWindowDimensions';

/**
 * Header Component
 *
 * This component displays the application's header, including the logo, navigation,
 * user authentication controls, and a dropdown user details card.
 *
 * @component
 * @param {Object} props - Component properties.
 * @param {Object} [props.style] - Additional styles to apply to the header.
 * @returns {JSX.Element} The rendered Header component.
 */
const Header = () => {
  // Access user details and authentication status from context
  const { userDetails, isLoggedIn, setUserDetails, setIsLoggedIn } =
    useContext(UserContext);

  const { width: windowWidth, height: windowHeight } = useWindowDimensions();

  // State for displaying user details dropdown
  const [showUserDetailsCard, setShowUserDetailsCard] = useState(false);

  // Refs for detecting clicks outside the dropdown
  const userLogoRef = useRef(null);
  const userDetailsCardRef = useRef(null);

  // Hook for navigation
  const navigate = useNavigate();
  const location = useLocation();

  /**
   * Toggles the visibility of the user details dropdown.
   */
  const toggleUserDetailsCard = () => {
    setShowUserDetailsCard((prev) => !prev);
  };

  /**
   * Closes the user details dropdown.
   */
  const closeUserDetailsCard = () => setShowUserDetailsCard(false);

  /**
   * Logs out the user by sending a request to the backend and clearing session data.
   */
  const handleLogout = async () => {
    try {
      const session_token = localStorage.getItem('session_token');
      if (!session_token) {
        window.location.reload();
        return;
      }
      const data = {
        email: userDetails?.email,
        session_token: session_token,
      };
      const response = await fetch(
        `${process.env.REACT_APP_SERVER_URL}/u/logout`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          // credentials: 'include',
          body: JSON.stringify(data),
        },
      );
      const responseData = await response.json();
      if (response.status === 200) {
        localStorage.removeItem('uid');
        localStorage.removeItem('u_e');
        localStorage.removeItem('session_token');
        navigate('/');
        setUserDetails({});
        setIsLoggedIn(false);
      } else {
        localStorage.removeItem('u_e');
        localStorage.removeItem('session_token');
        navigate('/login');
      }
    } catch (error) {
      console.log(error);
    }
  };

  const handleDashboardButtonClick = () => {
    if (location.pathname !== '/') {
      navigate('/');
    }
  };

  /**
   * Detects clicks outside the user details dropdown and closes it.
   * Runs when `showUserDetailsCard` changes.
   */
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        userDetailsCardRef.current &&
        !userDetailsCardRef.current.contains(event.target) &&
        userLogoRef.current &&
        !userLogoRef.current.contains(event.target)
      ) {
        closeUserDetailsCard();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showUserDetailsCard]);

  useEffect(() => {}, [showUserDetailsCard]);

  return (
    <>
      {/* <div className={cssClass['header']} style={{...style}}>
        <div className={cssClass['header-left']}>
          <img
            src={setuHeaderImage}
            className={cssClass['header-logo']}
            alt=""
          />
        </div>
      </div> */}

      <div className={cssClass['header']}>
        <div className={cssClass['logo']}>
          <a href="/">
            <img
              src={windowWidth <= 768 ? setuLogoSmall : setuHeaderImage}
              alt="logo"
            />
          </a>

          {/* <div className={cssClass['nav']}>
            <ul>
              <li>About DAP Academy</li>
            </ul>
          </div> */}
        </div>
        <div className={cssClass['actions']}>
          <button
            className={cssClass['dashboard-btn']}
            onClick={handleDashboardButtonClick}
          >
            Dashboard
          </button>
          {/* <p className={cssClass['login']}>Login</p> */}
          {isLoggedIn !== null && isLoggedIn ? (
            <div className={cssClass['user-ct']}>
              <p
                className={cssClass['user-logo']}
                onClick={toggleUserDetailsCard}
                ref={userLogoRef}
              >
                {userDetails?.email?.[0]?.toUpperCase() || 'A'}
              </p>
              <AnimatePresence>
                {showUserDetailsCard && (
                  <motion.div
                    initial={{ opacity: 0, y: -10, scale: 0.95 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: -10, scale: 0.95 }}
                    transition={{ duration: 0.3, ease: 'easeOut' }}
                    className={cssClass['user-details-card']}
                    ref={userDetailsCardRef}
                  >
                    <div className={cssClass['user-name']}>
                      {userDetails?.name}
                    </div>
                    <div className={cssClass['user-email']}>
                      {userDetails?.email}
                    </div>
                    <button
                      className={cssClass['logout-btn']}
                      onClick={handleLogout}
                    >
                      Logout
                    </button>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          ) : isLoggedIn !== null && !isLoggedIn ? (
            <p onClick={() => navigate('/login')} className={cssClass['login']}>
              Login
            </p>
          ) : (
            <></>
          )}
          {/* <img src={menu} className={cssClass['menu-icon']} alt="menu-icon" /> */}
        </div>
      </div>
    </>
  );
};

export default Header;
